import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EUserRole } from '__generated__/globalTypes';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { motion } from 'framer-motion';
import { CurrentUserWorldMaps } from 'graphql/query/__generated__/CurrentUserWorldMaps';
import { WORLD_MAP_CURRENT_USER_QUERY } from 'graphql/query/worldQuery';
import { ANIMATION_OPEN } from 'libs/styles/animations';
import { EBorders, EColors, EFonts, EHovers } from 'libs/styles/styling';
import _ from 'lodash';
import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { GlobalContext } from 'session/state/context';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
}

export const MapList = (props: Props): JSX.Element => {
    const query = useQuery<CurrentUserWorldMaps>(WORLD_MAP_CURRENT_USER_QUERY);
    const maps = query.data?.currentUserWorldMaps ?? [];
    const globalContext = useContext(GlobalContext);
    const currentUser = globalContext.state.loggedUser;

    const shouldUseLegacyEditor = currentUser?.userId === 'kontakt';

    return (
        <S_MapList className={props.className}>
            <S_Header>
                <S_Icon icon={['fal', 'list']} />
                <S_Head>{Localization.YOUR_MAPS}</S_Head>
            </S_Header>
            {query.loading && (
                <S_LoadingBlock>
                    <S_Loading icon={['far', 'spinner']} spin />
                </S_LoadingBlock>
            )}
            <motion.div {...ANIMATION_OPEN}>
                <S_Items>
                    {_.orderBy(maps, 'timeUpdated', 'desc')?.map((map, index) => {
                        return (
                            <S_Item key={map.worldMapId} $odd={index % 2 === 0}>
                                <S_Left>
                                    <S_Row1>
                                        <S_TitleLink to={`/map/${map.worldMapId}`}>
                                            <S_Title>
                                                {map.title !== ''
                                                    ? map.title
                                                    : Localization.UNNAMED_MAP}
                                            </S_Title>
                                        </S_TitleLink>
                                    </S_Row1>

                                    <S_Row2>
                                        <Link to={`/detail/${map.worldMapId}`}>
                                            {Localization.EDIT_CONTENT}
                                        </Link>
                                        {currentUser?.role === EUserRole.ADMIN && (
                                            <>
                                                &nbsp;·&nbsp;
                                                <Link to={`/edit/${map.worldMapId}`}>
                                                    {Localization.ADVANCED_EDITOR}
                                                </Link>
                                                {map.customMapUrl && (
                                                    <>
                                                        &nbsp;·&nbsp;
                                                        <Link to={`/m/${map.customMapUrl}`}>
                                                            {Localization.CUSTOM_URL}
                                                        </Link>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </S_Row2>
                                </S_Left>
                                <S_Right>
                                    <Link
                                        to={`/${shouldUseLegacyEditor ? 'edit' : 'minute'}/${
                                            map.worldMapId
                                        }`}
                                    >
                                        <ActionButton
                                            icon={{ iconName: 'hammer' }}
                                            text={Localization.EDIT}
                                        />
                                    </Link>
                                </S_Right>
                            </S_Item>
                        );
                    })}
                </S_Items>
            </motion.div>
        </S_MapList>
    );
};

const S_MapList = styled.div`
    border-radius: 7px;
    background: #efefef;
    box-shadow: 5px 5px 10px 0px rgba(0, 0, 0, 0.1);
    border-radius: 7px;
    overflow: hidden;
    margin: 0 0 20px 0;
    border: 1px solid ${EBorders.DARK};
`;

const S_Header = styled.div`
    padding: 15px 20px;
    background-color: ${EColors.GREY5};
    border-bottom: 1px solid #d3d3d3;
    display: flex;
    align-items: center;
`;

const S_Icon = styled(FontAwesomeIcon)`
    font-size: 25px;
    color: ${EColors.DARK};
    margin: 0 15px 0 0;
`;

const S_Head = styled.div`
    font-size: 16px;
    color: ${EColors.DARK};
    font-family: ${EFonts.Medium};
`;

const S_Items = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 12px;
    width: 100%;
`;

const S_Item = styled.div<{ $odd: boolean }>`
    padding: 15px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    user-select: none;
    border-bottom: 1px solid #dadada;
    ${(props) => {
        return css`
            background: ${props.$odd ? 'rgba(0, 0, 0, 0.02)' : 'unset'};
            &:hover {
                box-shadow: ${EHovers.LIGHT4};
            }
        `;
    }}
`;

const S_Left = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_TitleLink = styled(Link)`
    width: fit-content;
    &:hover {
        text-decoration: underline;
    }
`;

const S_Title = styled.div`
    color: ${EColors.DARK};
    font-size: 16px;
    font-family: ${EFonts.Medium};
    width: fit-content;
`;

const S_Right = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const S_Row1 = styled.div``;

const S_Row2 = styled.div`
    margin: 5px 0 0px 0;
    a:hover {
        text-decoration: underline;
    }
`;

const S_LoadingBlock = styled.div`
    display: flex;
    justify-content: center;
    margin: 50px 0;
`;

const S_Loading = styled(FontAwesomeIcon)`
    color: ${EColors.DARK};
    font-size: 30px;
`;
